/**
 * Shortest substring the assistant transcript search will run. The search is a
 * sequential scan over serialized message payloads, so a one- or two-character
 * needle costs a full scan and matches almost every thread — worthless as a
 * filter and expensive to produce.
 *
 * Shared so the operator UI can say "type at least N characters" using the same
 * number the server enforces, rather than reporting a short query as "no
 * matches".
 */
export const MIN_TRANSCRIPT_SEARCH_QUERY_LENGTH = 3;

/**
 * Payload keys whose value the transcript search ignores. `thinkingSignature`
 * is the Anthropic extended-thinking integrity token: multi-kilobyte base64,
 * never human-read, and shown only as a truncated preview. Left in the
 * haystack it matches almost any short needle, so a search returns threads the
 * operator opens to find nothing highlighted.
 *
 * A key belongs here only if its value can never contain a quote — see
 * {@link OPAQUE_TRANSCRIPT_VALUE_PATTERN} for why. Base64 satisfies that; free
 * text does not.
 */
export const OPAQUE_TRANSCRIPT_PAYLOAD_KEYS = ['thinkingSignature'] as const;

/**
 * Matches `"<opaque key>": "<value>"` in a serialized payload, for excising the
 * value before either side searches or counts. Written to fit both
 * serializations: Postgres's `jsonb::text` puts a space after the colon and
 * `JSON.stringify` does not, which `\s*` absorbs.
 *
 * `[^"]*` is not JSON-string-aware — it stops at the quote of an escaped `\"`
 * and would leave the tail of such a value in the haystack. That is sound only
 * because every key it covers holds base64, which has no quote to escape;
 * covering a value that can contain one needs a real JSON-string pattern.
 *
 * A pattern string rather than a `RegExp` so Postgres's `regexp_replace` and
 * JavaScript's `RegExp` can be handed the same rule — the two counts must agree
 * or a thread badge contradicts the message badges under it.
 */
export const OPAQUE_TRANSCRIPT_VALUE_PATTERN = `"(?:${OPAQUE_TRANSCRIPT_PAYLOAD_KEYS.join('|')})":\\s*"[^"]*"`;

/**
 * Matches the space Postgres's `jsonb::text` writes after a `:` or `,` and
 * `JSON.stringify` does not. Dropping it gives the two serializations one
 * spelling of the same payload, so a needle carrying JSON punctuation —
 * `role": "assistant` — cannot count on the server and come up empty on the
 * client. Apply it to the needle as well as to both haystacks, or the
 * normalized haystack stops matching the query the operator typed.
 *
 * It is deliberately not JSON-aware: it also collapses `", "` inside a string
 * value. That is harmless precisely because it is unconditional — both sides
 * and the needle collapse identically, so the three still agree. The only
 * effect is that a needle spelled `a, b` also finds `a,b`.
 *
 * A pattern string, not a `RegExp`: the two engines spell the backreference
 * differently (`\1` in Postgres, `$1` in JavaScript), so each side supplies its
 * own replacement.
 */
export const JSON_PUNCTUATION_PADDING_PATTERN = '([:,]) ';
